import type { Job } from '../modules/dashboard/components/JobCard';
import type { UserProfile } from '../types/userProfile';

export type MarketPosition =
  | 'well_above'
  | 'above_average'
  | 'at_market'
  | 'below_average'
  | 'well_below';

export interface SalaryIntelligence {
  estimatedMinLpa: number;
  estimatedMaxLpa: number;
  estimatedLabel: string;
  expectedMinLpa: number | null;
  expectedMaxLpa: number | null;
  expectedLabel: string | null;
  marketPosition: MarketPosition;
  underpaidPercent: number | null;
  isEstimated: boolean;
  hasData: boolean;
}

export interface SalaryIntelJobInput {
  salaryMin?: number | null;
  salaryMax?: number | null;
  salaryCurrency?: string | null;
  compensationText?: string | null;
  description?: string | null;
  experienceLevel?: string | null;
  title?: string | null;
}

export interface SalaryIntelProfileInput {
  experienceYears?: number | null;
  salaryExpectationInr?: number | null;
  currentSalary?: string | null;
}

// ---------------------------------------------------------------------------
// Parsing & formatting
// ---------------------------------------------------------------------------

function roundLpa(value: number): number {
  return Math.round(value * 10) / 10;
}

function formatLpa(value: number): string {
  const rounded = roundLpa(value);
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function formatLpaRange(minLpa: number, maxLpa: number): string {
  if (minLpa === maxLpa) return `₹${formatLpa(minLpa)} LPA`;
  return `₹${formatLpa(minLpa)}–${formatLpa(maxLpa)} LPA`;
}

export function formatSalaryLpaFromInr(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value) || value <= 0) return null;
  const lpa = value >= 100_000 ? value / 100_000 : value;
  return formatLpaRange(lpa, lpa);
}

export function highestSalaryLabelFromJobs(
  jobs: Array<{
    salary_min?: number | null;
    salary_max?: number | null;
    salaryMin?: number | null;
    salaryMax?: number | null;
  }>,
): string | null {
  let best = 0;
  for (const job of jobs) {
    const candidate = job.salary_max ?? job.salary_min ?? job.salaryMax ?? job.salaryMin ?? 0;
    if (candidate > best) best = candidate;
  }
  return formatSalaryLpaFromInr(best);
}

function inrToLpa(inr: number): number {
  if (inr >= 100_000) return inr / 100_000;
  return inr;
}

function normalizeSalaryBound(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return inrToLpa(value);
}

export function parseInrAmount(raw: string | null | undefined): number | null {
  if (!raw?.trim()) return null;
  const text = raw.trim().toLowerCase();

  const lpaMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:lpa|lakhs?|l\b)/);
  if (lpaMatch) {
    const lpa = parseFloat(lpaMatch[1]);
    return Number.isFinite(lpa) && lpa > 0 ? lpa * 100_000 : null;
  }

  const digits = text.replace(/[^\d.]/g, '');
  const value = parseFloat(digits);
  if (!Number.isFinite(value) || value <= 0) return null;
  if (value >= 100_000) return value;
  if (value < 200) return value * 100_000;
  return value;
}

function parseLpaRangeFromText(text: string): { min: number; max: number } | null {
  const normalized = text
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .slice(0, 4000);

  const patterns: RegExp[] = [
    /(?:₹|rs\.?|inr)\s*(\d[\d,.]*)\s*(?:-|–|to)\s*(?:₹|rs\.?|inr)?\s*(\d[\d,.]*)\s*(?:lpa|lakhs?|l)\b/i,
    /(\d{1,2}(?:\.\d+)?)\s*[-–]\s*(\d{1,2}(?:\.\d+)?)\s*(?:lpa|lakhs?|l)\b/i,
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (!match?.[1] || !match[2]) continue;
    const min = parseFloat(match[1].replace(/,/g, ''));
    const max = parseFloat(match[2].replace(/,/g, ''));
    if (Number.isFinite(min) && Number.isFinite(max) && min > 0 && max >= min) {
      return { min, max };
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Market benchmarks (India tech — indicative bands by experience)
// ---------------------------------------------------------------------------

function marketBandLpa(years: number): { min: number; max: number; mid: number } {
  if (years < 2) return { min: 4, max: 8, mid: 6 };
  if (years < 5) return { min: 10, max: 18, mid: 14 };
  if (years < 8) return { min: 18, max: 28, mid: 23 };
  if (years < 12) return { min: 28, max: 45, mid: 36.5 };
  return { min: 40, max: 70, mid: 55 };
}

function roleMultiplier(title?: string | null, experienceLevel?: string | null): number {
  const text = `${title ?? ''} ${experienceLevel ?? ''}`.toLowerCase();
  if (/\b(principal|staff|director|vp|head)\b/.test(text)) return 1.35;
  if (/\b(senior|sr\.?|lead|architect)\b/.test(text)) return 1.2;
  if (/\b(junior|jr\.?|fresher|intern|entry|associate)\b/.test(text)) return 0.78;
  return 1;
}

function inferRangeFromProfile(
  title?: string | null,
  experienceLevel?: string | null,
  experienceYears?: number | null,
): { min: number; max: number } {
  const years = experienceYears ?? 5;
  const band = marketBandLpa(years);
  const mult = roleMultiplier(title, experienceLevel);
  return { min: roundLpa(band.min * mult), max: roundLpa(band.max * mult) };
}

function resolveJobRange(input: SalaryIntelJobInput): { min: number; max: number; isEstimated: boolean } | null {
  const currency = (input.salaryCurrency ?? 'INR').toUpperCase();
  const minRaw = input.salaryMin;
  const maxRaw = input.salaryMax;

  if (currency === 'INR' && (minRaw != null || maxRaw != null)) {
    const min = minRaw != null ? normalizeSalaryBound(minRaw) : 0;
    const max = maxRaw != null ? normalizeSalaryBound(maxRaw) : 0;

    if (min > 0 && max > 0) {
      return { min: roundLpa(Math.min(min, max)), max: roundLpa(Math.max(min, max)), isEstimated: false };
    }
    if (min > 0) {
      return { min: roundLpa(min * 0.88), max: roundLpa(min * 1.12), isEstimated: true };
    }
    if (max > 0) {
      return { min: roundLpa(max * 0.88), max: roundLpa(max * 1.12), isEstimated: true };
    }
  }

  const fromComp = input.compensationText ? parseLpaRangeFromText(input.compensationText) : null;
  if (fromComp) {
    return { min: roundLpa(fromComp.min), max: roundLpa(fromComp.max), isEstimated: false };
  }

  const fromDesc = input.description ? parseLpaRangeFromText(input.description) : null;
  if (fromDesc) {
    return { min: roundLpa(fromDesc.min), max: roundLpa(fromDesc.max), isEstimated: true };
  }

  const inferred = inferRangeFromProfile(input.title, input.experienceLevel, null);
  return { min: inferred.min, max: inferred.max, isEstimated: true };
}

function computeMarketPosition(jobMid: number, experienceYears: number | null): MarketPosition {
  const years = experienceYears ?? 5;
  const marketMid = marketBandLpa(years).mid;
  const ratio = jobMid / marketMid;

  if (ratio >= 1.15) return 'well_above';
  if (ratio >= 1.05) return 'above_average';
  if (ratio >= 0.95) return 'at_market';
  if (ratio >= 0.85) return 'below_average';
  return 'well_below';
}

function computeExpectedRange(expectationInr: number | null | undefined): { min: number; max: number } | null {
  if (expectationInr == null || !Number.isFinite(expectationInr) || expectationInr <= 0) return null;
  const center = inrToLpa(expectationInr);
  const spread = Math.max(1.5, center * 0.07);
  return {
    min: roundLpa(Math.max(1, center - spread)),
    max: roundLpa(center + spread),
  };
}

function computeUnderpaidPercent(
  currentSalary: string | null | undefined,
  experienceYears: number | null | undefined,
): number | null {
  const currentInr = parseInrAmount(currentSalary);
  if (currentInr == null) return null;

  const currentLpa = inrToLpa(currentInr);
  const marketMid = marketBandLpa(experienceYears ?? 5).mid;
  if (currentLpa >= marketMid * 0.92) return null;

  const pct = Math.round((1 - currentLpa / marketMid) * 100);
  return pct > 0 ? pct : null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function computeSalaryIntelligence(
  job: SalaryIntelJobInput,
  profile?: SalaryIntelProfileInput | null,
): SalaryIntelligence {
  const jobRange = resolveJobRange(job);
  const experienceYears = profile?.experienceYears ?? null;

  if (!jobRange) {
    return {
      estimatedMinLpa: 0,
      estimatedMaxLpa: 0,
      estimatedLabel: '',
      expectedMinLpa: null,
      expectedMaxLpa: null,
      expectedLabel: null,
      marketPosition: 'at_market',
      underpaidPercent: null,
      isEstimated: true,
      hasData: false,
    };
  }

  const jobMid = (jobRange.min + jobRange.max) / 2;
  const expected = computeExpectedRange(profile?.salaryExpectationInr);
  const underpaidPercent = computeUnderpaidPercent(profile?.currentSalary, experienceYears);

  return {
    estimatedMinLpa: jobRange.min,
    estimatedMaxLpa: jobRange.max,
    estimatedLabel: formatLpaRange(jobRange.min, jobRange.max),
    expectedMinLpa: expected?.min ?? null,
    expectedMaxLpa: expected?.max ?? null,
    expectedLabel: expected ? formatLpaRange(expected.min, expected.max) : null,
    marketPosition: computeMarketPosition(jobMid, experienceYears),
    underpaidPercent,
    isEstimated: jobRange.isEstimated,
    hasData: true,
  };
}

export function computeSalaryIntelligenceForJob(
  job: Job,
  profile?: UserProfile | null,
): SalaryIntelligence {
  return computeSalaryIntelligence(
    {
      salaryMin: job.salary_min,
      salaryMax: job.salary_max,
      salaryCurrency: job.salary_currency,
      compensationText: job.compensation_text,
      description: job.description,
      experienceLevel: job.experience_level,
      title: job.title,
    },
    profile
      ? {
          experienceYears: profile.experienceYears,
          salaryExpectationInr: profile.salaryExpectationInr,
          currentSalary: profile.currentSalary,
        }
      : null,
  );
}

/** Representative demo values for marketing / copilot preview. */
export function buildDemoSalaryIntelligence(): SalaryIntelligence {
  return {
    estimatedMinLpa: 18,
    estimatedMaxLpa: 24,
    estimatedLabel: formatLpaRange(18, 24),
    expectedMinLpa: 20,
    expectedMaxLpa: 23,
    expectedLabel: formatLpaRange(20, 23),
    marketPosition: 'above_average',
    underpaidPercent: 12,
    isEstimated: false,
    hasData: true,
  };
}
